import React, { useEffect, useMemo } from 'react';
import { useAudioContext } from '../providers/AudioProvider';
import {
    StyleSheet,
    Text,
    TouchableOpacity,
    useWindowDimensions,
    View,
} from 'react-native';
import getGlobalStyles from '../globalStyles';
import { useThemeSettings } from '../ThemeProvider';
import { CustomIcon } from './CustomIcon';
import { TrackedTouchable } from '../analytics/components';
import FadeImage from './FadeImage';
import AudioProgressBar from './AudioProgressBar';
import { ModalType, useModalStore } from '../store/modalStore';
import GistFullToggle from './GistFullToggle';
import { useActiveArticle } from '../features/news-feed/hooks/useActiveArticle';
import { useAudioFunctions } from '../hooks/useAudioFunctions';
import VerticalGestureHandler from './VerticalGestureHandler';
import { usePlatform } from '../providers/PlatformProvider';
import CustomLinearGradient, { GRADIENT_TYPE } from './CustomLinearGradient';
import { FULL_WIDTH } from '../shared/constants/dimension';
import { formatArticleDate } from '../shared/helpers/formatDate';
import { ROUTES } from '../shared/constants/routes';
import { useCollectionStore } from '../store/collectionStore';
import { useIsFocused, useRoute } from '@react-navigation/native';
import { getImageUri } from '../shared/helpers/utils';
import { useTrackScreenTime } from '../analytics/useTrackScreenTime';
import TourStep from './productTours/TourStep';
import { TOUR_TYPES } from '../store/useTourStore';
import FaviconStack from './FaviconStack';
import AppLoader from './AppLoader';

const formatRate = (value: number) => {
  return value % 1 !== 0 ? parseFloat(value.toFixed(1)) : value;
};
export default function AudioPlayer({ }: {}) {
    useTrackScreenTime(ROUTES.RADIO_PLAYER)
    const {
        isPlaying,
        duration,
        position,
        togglePlayback,
        seekForward,
        seekBackward,
        seekTo,
        mode,
        setMode,
        rate,
        toggleRate
    } = useAudioContext();
    const { showWebVersion } = usePlatform();
    const { selectedCollection } = useCollectionStore();
    const { height } = useWindowDimensions();
    const { activeArticle: article, setActiveArticleId } = useActiveArticle();
    const { toggleNext, togglePrevious, swipeUp, swipeDown } = useAudioFunctions();
    const { theme } = useThemeSettings();
    const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
    const { setModalShown } = useModalStore();
    const isFocused = useIsFocused();
    const { setPlaybackFocus } = useAudioContext();
    const route = useRoute();

    useEffect(() => {
        return () => {
            setPlaybackFocus(false);
        }
    }, [])
    useEffect(() => {
        setPlaybackFocus(isFocused);
    }, [isFocused]);

    useEffect(() => {
        setActiveArticleId((route?.params as any)?.articleId!);
    }, [(route?.params as any)?.articleId])

     if (!article?.id) {
        return <AppLoader isLoading />;
    }

    const child = <View style={[gStyles.centerContainer, { overflow: 'scroll', gap: 12, paddingHorizontal: 12 }]}>
        <FadeImage
            source={getImageUri(article?.image!)}
            style={[styles.albumArt, { borderColor: theme?.colors?.secondary }]}
            resizeMode="cover"
        />
        <AudioProgressBar
            position={position}
            duration={duration}
            onSeek={seekTo}
        />
        <View style={{ width: FULL_WIDTH }}>
             <View style={[gStyles.centerRow, { justifyContent: 'space-around', width: FULL_WIDTH, gap: 8, alignItems : "flex-start" }]}>
                <Text style={{ fontSize: 16, flex: 1, color: theme.colors.textPrimary, paddingLeft: 2 }}>
                    {article?.title}
                </Text>
                <TourStep order={1} type={TOUR_TYPES.AUDIO_PLAYER}>
                    <TrackedTouchable id="speedToggle" context={{ cta: "AUDIO_SPEED", rate }} style={[gStyles.buttonStyle, gStyles.buttonCircle]} onPress={toggleRate}>
                        <Text style={{ color: theme.colors.primary, fontSize: 11, minWidth: 24, textAlign: 'center' }}>
                             {`${formatRate(rate)}x`}
                        </Text>
                    </TrackedTouchable>
                </TourStep>
                <TrackedTouchable context={{ cta: "SHARE_ARTICLE", rate }} style={[gStyles.buttonStyle, gStyles.buttonCircle]} onPress={() => setModalShown(ModalType.SHARE_ARTICLE, { article, collection: selectedCollection, sharedFrom: ROUTES.RADIO_PLAYER })}>
                    <CustomIcon icon="share" size={16} />
                </TrackedTouchable>
            </View>
            <Text style={{ color: theme.colors.textPrimary }}> {formatArticleDate(article?.date!, 'en-US')}</Text>
        </View>

        <View style={[gStyles.centerRow, gStyles.scale, { gap: 24 }]}>
            <TrackedTouchable context={{ cta: "AUDIO_PREVIOUS" }} onPress={togglePrevious}>
                <CustomIcon size={24} icon={'previous'} />
            </TrackedTouchable>
            <TrackedTouchable context={{ cta: "BACKWARD_15_SECONDS" }} onPress={seekBackward}>
                <CustomIcon size={24} icon={'backward'} />
            </TrackedTouchable>
            <TrackedTouchable context={{ cta: isPlaying ? "AUDIO_PLAY" : "AUDIO_PAUSE", article_id: article?.id }} style={[gStyles.buttonStyle, gStyles.buttonCircle, styles.playPause]} onPress={togglePlayback}>
                <CustomIcon size={32} icon={isPlaying ? 'pause' : 'play'} />
            </TrackedTouchable>
            <TrackedTouchable context={{ cta: "FORWARD_15_SECONDS" }} onPress={seekForward}>
                <CustomIcon size={24} icon={'forward'} />
            </TrackedTouchable>
            <TrackedTouchable context={{ cta: "AUDIO_NEXT" }} onPress={toggleNext}>
                <CustomIcon size={24} icon={'next'} />
            </TrackedTouchable>
        </View>

        <View style={[styles.controls, {
            width: FULL_WIDTH, flexDirection: 'row',
            justifyContent: 'flex-end'
        }]}>
            {/*  <GistFullToggle value={mode} onChange={setMode} /> */}
            <FaviconStack
                favicons={article?.favicons ?? []}
                count={0}
                style={styles.sourceStyle}
                articleId={article?.id}
            />
            <TourStep order={2} type={TOUR_TYPES.AUDIO_PLAYER}>
                <TouchableOpacity id="audioPlaylist" style={[gStyles.buttonStyle, gStyles.buttonCircle, { padding: 6 }]}
                    onPress={() => setModalShown(ModalType.AUDIO_LIST)}>
                    <CustomIcon size={16} icon="queue" color={theme?.colors?.primary} />
                </TouchableOpacity>
            </TourStep>

        </View>
    </View>;
    return (
        <VerticalGestureHandler swipeUp={swipeUp} swipeDown={swipeDown}>
            {showWebVersion ? <CustomLinearGradient gradientType={GRADIENT_TYPE.APP_CARD} style={[gStyles.webCard, { height: height - 160 }]}>
                {child}
            </CustomLinearGradient> : child}
        </VerticalGestureHandler>
    );
}

const styles = StyleSheet.create({
    playPause: {
        borderRadius: 35,
        width: 72,
        height: 72,
    },
    albumArt: {
        borderRadius: 12,
        marginBottom: 20,
        textAlign: 'center',
        borderWidth: 1,
        width: 200,
        height: 200,
    },
    timeRow: {
        flexDirection: 'row',
        marginBottom: 10,
    },
    controls: {
        flexDirection: 'row',
        gap: 16,
        alignItems: 'center',
    },
    audioSpeed: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 20,
    },
    closeButton: {
        paddingLeft: 12,
        position: 'absolute',
        top: 80,
        right: 20,
        zIndex: 10,
    },

    closeCircle: {
        width: 30,
        height: 30,
        borderRadius: 15,
        borderWidth: 2,
        justifyContent: "center",
        alignItems: "center",
    },

    closeText: {
        fontSize: 14,
        fontWeight: "900",
    },
      sourceStyle : {
        position: 'absolute',
        bottom: 2,
        left: 0,
        right: 0,
    }
});
